import { cn } from '@/lib/utils';
import { Square, Smartphone, Monitor } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export type FormatType = 'square' | 'portrait' | 'horizontal';

interface Format {
  id: FormatType;
  labelKey: 'square' | 'portrait' | 'horizontal';
  dimensions: string;
  icon: React.ReactNode;
  aspectRatio: string;
}

const formatDefinitions: Format[] = [
  {
    id: 'square',
    labelKey: 'square',
    dimensions: '1080×1080',
    icon: <Square className="h-5 w-5" />,
    aspectRatio: '1:1',
  },
  {
    id: 'portrait',
    labelKey: 'portrait',
    dimensions: '1080×1920',
    icon: <Smartphone className="h-5 w-5" />,
    aspectRatio: '9:16',
  },
  {
    id: 'horizontal',
    labelKey: 'horizontal',
    dimensions: '1920×1080',
    icon: <Monitor className="h-5 w-5" />,
    aspectRatio: '16:9',
  },
];

interface FormatSelectorProps {
  selected: FormatType[];
  onChange: (formats: FormatType[]) => void;
  disabled?: boolean;
}

export function FormatSelector({ selected, onChange, disabled }: FormatSelectorProps) {
  const { t } = useLanguage();

  const toggleFormat = (formatId: FormatType) => {
    if (disabled) return;
    
    if (selected.includes(formatId)) {
      onChange(selected.filter((id) => id !== formatId));
    } else {
      onChange([...selected, formatId]);
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-foreground">
        {t('formats', 'outputFormats')} <span className="text-muted-foreground">{t('formats', 'creditEach')}</span>
      </label>
      <div className="grid grid-cols-3 gap-3">
        {formatDefinitions.map((format) => (
          <button
            key={format.id}
            onClick={() => toggleFormat(format.id)}
            disabled={disabled}
            className={cn(
              'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200',
              selected.includes(format.id)
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:border-primary/50 bg-secondary/30',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {format.icon}
            <span className="text-sm font-medium">{t('formats', format.labelKey)}</span>
            <span className="text-xs text-muted-foreground">{format.dimensions}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Export formats for use in OutputPanel
export const formats = formatDefinitions.map(f => ({
  id: f.id,
  label: f.labelKey,
  dimensions: f.dimensions,
  icon: f.icon,
  aspectRatio: f.aspectRatio,
}));
